import { useEffect, useMemo, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { apiUrl } from "../../utils/api";
import {
    registerBeteiligteUebernehmen,
    type RegisterBeteiligteErgebnis,
    type RegisterBeteiligter,
    type UebernahmeEintrag,
    type UebernahmeErgebnis,
} from "../../services/firmendatenService";
import { BeteiligungsrechercheDialog } from "./BeteiligungsrechercheDialog";
import { STATUS_MFU, STATUS_MFU_ALT } from "../../data/sonstigeStatusKatalog";
import { BEZ_KOMPLEMENTAERIN_FIRMA, VERWALTUNGSGESELLSCHAFT_HINWEIS } from "../../utils/komplementaerGmbh";
import {
    FIRMEN_ROLLEN,
    FIRMEN_ROLLE_HINWEIS,
    FIRMEN_ROLLE_LABEL,
    firmenRolleNoetig,
    istFirmenRolle,
    type FirmenRolle,
} from "../../utils/firmenGesellschafterArt";

/**
 * Auswahl und Anlage der im Handelsregister eingetragenen Personen.
 *
 * Zwei Schritte, weil beides gebraucht wird: erst **auswählen**, wer überhaupt
 * in die Akte gehört (ein Konzern bringt dreistellige Prokuristenzahlen mit),
 * dann **prüfen**, wie die neuen Kontakte heißen sollen. Das Register kennt
 * keine Anrede und schreibt Namen so, wie sie eingetragen wurden („Lars
 * Alexander - genannt Alexander - Künzer") — beides gehört korrigiert, bevor
 * ein Kontakt daraus wird.
 *
 * Die Optik kommt aus makeStyles statt aus `contactRedesign.css`: Der Dialog
 * hängt per Portal am `document.body` und damit außerhalb von `.kvp-rd` —
 * dort greifen die gescopten Regeln nicht, Buttons blieben grau.
 */

// Griffel akzeptiert in makeStyles keine CSS-Shorthands (border/padding/gap/font)
// — dafür gibt es `shorthands.*` bzw. die Longhand-Schreibweise.
const useStyles = makeStyles({
    kopfzeile: { fontSize: "13px", color: "#6b5a66", marginBottom: "14px", lineHeight: "1.45" },
    werkzeuge: {
        display: "flex",
        columnGap: "10px",
        rowGap: "8px",
        alignItems: "center",
        flexWrap: "wrap",
        marginBottom: "12px",
    },
    zaehler: { fontSize: "13px", color: "#6b5a66" },
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        ...shorthands.padding("9px", "14px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        backgroundColor: "#fff",
        color: "#7d2169",
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transitionDuration: "130ms",
        ":hover": { ...shorthands.borderColor("#cd8fbd"), backgroundColor: "#f8eef5" },
        ":disabled": { color: "#a596a1", cursor: "default", backgroundColor: "#f6f3f5" },
    },
    knopfStark: {
        backgroundColor: "#7d2169",
        color: "#fff",
        ...shorthands.borderColor("#7d2169"),
        ":hover": { backgroundColor: "#591149", ...shorthands.borderColor("#591149") },
        ":disabled": {
            backgroundColor: "#d9c6d4",
            ...shorthands.borderColor("#d9c6d4"),
            color: "#fff",
        },
    },
    liste: { maxHeight: "46vh", overflowY: "auto", overflowX: "hidden" },
    zeile: {
        display: "flex",
        ...shorthands.gap("10px"),
        alignItems: "flex-start",
        ...shorthands.padding("9px", "4px"),
        ...shorthands.borderBottom("1px", "solid", "#ece6ec"),
        cursor: "pointer",
    },
    name: {
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "14.5px",
        fontWeight: 700,
        lineHeight: 1.25,
        color: "#241522",
    },
    meta: { display: "block", fontSize: "12.5px", color: "#6b5a66", marginTop: "3px" },
    inhalt: { flexGrow: 1, minWidth: 0 },
    marke: { fontSize: "11.5px", fontWeight: 700, whiteSpace: "nowrap" },
    markeNeu: { color: "#7d2169" },
    markeVorhanden: { color: "#1f7a4d" },
    markeAehnlich: { color: "#a8620a" },
    // Komplementär-GmbH (T11): sichtbar, damit niemand sie vermisst, aber grau
    // und nicht wählbar.
    zeileAus: { cursor: "default", opacity: 0.6 },
    markeAus: { color: "#8a7f88" },
    verwaltung: { display: "block", fontSize: "12px", color: "#8a7f88", marginTop: "3px", fontStyle: "italic" },
    formZeile: {
        display: "grid",
        gridTemplateColumns:
            "minmax(120px, 1fr) minmax(140px, 1.4fr) minmax(140px, 1.4fr) minmax(140px, 1fr)",
        ...shorthands.gap("10px"),
        alignItems: "end",
        ...shorthands.padding("10px", "0"),
        ...shorthands.borderBottom("1px", "solid", "#ece6ec"),
        "@media (max-width: 720px)": { gridTemplateColumns: "1fr 1fr" },
    },
    feld: { display: "flex", flexDirection: "column", ...shorthands.gap("4px"), minWidth: 0 },
    feldBreit: { gridColumnStart: 1, gridColumnEnd: -1 },
    beschriftung: { fontSize: "11.5px", fontWeight: 700, color: "#6b5a66" },
    eingabe: {
        ...shorthands.padding("9px", "10px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "13.5px",
        fontWeight: 600,
        color: "#241522",
        backgroundColor: "#fff",
        minWidth: 0,
        ":focus": { ...shorthands.borderColor("#7d2169"), outlineStyle: "none" },
    },
    rollenZeile: { fontSize: "12.5px", color: "#6b5a66", marginBottom: "6px", marginTop: "10px" },
    fehler: { fontSize: "12.5px", color: "#b42318", marginTop: "10px" },
    hinweis: {
        fontSize: "12.5px",
        color: "#7a5a18",
        backgroundColor: "#fdf6e3",
        ...shorthands.padding("8px", "10px"),
        ...shorthands.borderRadius("8px"),
        marginBottom: "10px",
        lineHeight: 1.45,
    },
    anteilMarke: { fontSize: "12px", color: "#7d2169", fontWeight: 700 },
    weitersuchen: {
        ...shorthands.borderWidth("0"),
        ...shorthands.padding("0"),
        backgroundColor: "transparent",
        color: "#7d2169",
        fontFamily: "inherit",
        fontSize: "12px",
        fontWeight: 700,
        cursor: "pointer",
        textDecorationLine: "underline",
        marginTop: "4px",
        display: "block",
    },
    verweis: {
        ...shorthands.borderWidth("0"),
        ...shorthands.padding("0"),
        backgroundColor: "transparent",
        color: "#7d2169",
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: 700,
        cursor: "pointer",
        textDecorationLine: "underline",
    },
    fuss: { display: "flex", ...shorthands.gap("10px"), justifyContent: "flex-end" },
    ergebnis: { fontSize: "14px", color: "#241522" },
    ergebnisFehler: { color: "#b42318", marginTop: "8px", marginLeft: "18px" },
    haken: { marginTop: "4px" },
});

type Schritt = "auswahl" | "pruefen" | "ergebnis";

/**
 * Bearbeitbare Felder je Vorschlag.
 *
 * Geprüft wird seit 08/2026 JEDER ausgewählte Eintrag, nicht nur die neu
 * anzulegenden: Bei einem bereits vorhandenen Kontakt bleibt der Name gesperrt
 * — an fremden Stammdaten wird hier nichts geändert —, aber der Anteil gehört
 * zur Verbindung und muss auch dort einzutragen sein. Aus einer per
 * Texterkennung gelesenen Gesellschafterliste kommt er oft gar nicht mit.
 */
type Entwurf = {
    anrede: string;
    vorname: string;
    nachname: string;
    geburtsdatum: string;
    anteil: string;
    /**
     * Rolle innerhalb des Beziehungstyps. Vorbelegt aus `beziehungsfunktion`
     * (sicher) bzw. `rollenVorschlag` (zu bestätigen) — leer heißt: schlichte
     * Gesellschafterstellung ohne benannte Rolle.
     */
    funktion: string;
    /** Kontaktstatus. Nur bei Personen bedienbar. */
    status: string;
    /** Gesellschaft als schlichte Gesellschafterin: gewählte Art (leer = noch keine). */
    firmenRolle: string;
    /** Vorhandener Kontakt: Stammdaten bleiben, nur der Anteil ist bedienbar. */
    gesperrt: boolean;
};

/** Fällt der Register-Import auf diesen Status zurück (Backend: REGISTER_STATUS_DEFAULT). */
const STATUS_DEFAULT = STATUS_MFU;

/**
 * Rollen, die im Prüfen-Schritt zur Auswahl stehen, wenn ein Vorschlag anliegt.
 * Bewusst kurz: der Vorschlag, seine Alternative und „ohne benannte Rolle".
 * Die Werte müssen zeichengenau den `Verantwortlichkeit.bezeichnung`-Einträgen
 * entsprechen (Migration 202608150003) — ein abweichender Text erzeugt eine
 * Freitext-Rolle und damit einen Matrix-Verstoß.
 */
const KG_ROLLEN = ["Komplementärin (Gesellschaft)", "Komplementär", "Kommanditist"];

const schluessel = (b: RegisterBeteiligter, i: number) =>
    `${i}-${b.rolle}-${b.nachname ?? b.bezeichnung ?? ""}-${b.geburtsdatum ?? ""}`;

const anzeigename = (b: RegisterBeteiligter) =>
    b.art === "person"
        ? [b.vorname, b.nachname].filter(Boolean).join(" ")
        : b.bezeichnung ?? "";

/**
 * Die Komplementär-GmbH einer GmbH & Co. KG wird nie angelegt (T11): weder in
 * der Akte noch über die Anlagemaske vorgemerkt. Das Backend markiert sie
 * (`verwaltungsgesellschaft`) und überspringt sie beim Übernehmen zusätzlich.
 */
const waehlbar = (b: RegisterBeteiligter) => !b.verwaltungsgesellschaft;

/** Im Prüfen-Schritt als Komplementärin bestätigt → ebenfalls Verwaltungsgesellschaft. */
const alsKomplementaerinBestaetigt = (b: RegisterBeteiligter, funktion?: string) =>
    b.art === "organisation" && (funktion ?? "").trim() === BEZ_KOMPLEMENTAERIN_FIRMA;

/** Schlüssel aller Einträge, auf die `bedingung` zutrifft — mit dem Index in `liste`. */
const schluesselWo = (liste: RegisterBeteiligter[], bedingung: (b: RegisterBeteiligter) => boolean) =>
    liste.flatMap((b, i) => (bedingung(b) ? [schluessel(b, i)] : []));

const datum = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString("de-DE") : null;

export function RegisterBeteiligteDialog({
    contactId,
    daten,
    onClose,
    onUebernommen,
    onEintraege,
    onDokumentOeffnen,
}: {
    /** Bestehende Firma — dann wird direkt angelegt und verbunden. */
    contactId?: number;
    daten: RegisterBeteiligteErgebnis;
    onClose: () => void;
    /** Nach dem Anlegen: Detailansicht neu laden. */
    onUebernommen?: () => void;
    /**
     * Anlagemaske: Die Firma existiert noch nicht — die fertigen Einträge
     * gehen zurück an das Formular und werden nach dem Speichern übernommen.
     */
    onEintraege?: (eintraege: UebernahmeEintrag[]) => void;
    /**
     * Das Belegdokument öffnen. Bei einer per Texterkennung gelesenen
     * Gesellschafterliste ist das keine Zugabe, sondern die Voraussetzung fürs
     * Prüfen — ohne das Original ist nicht zu sehen, was die Erkennung
     * verschluckt hat.
     */
    onDokumentOeffnen?: () => void;
}) {
    const s = useStyles();
    const [schritt, setSchritt] = useState<Schritt>("auswahl");
    const [anreden, setAnreden] = useState<string[]>(["Herr", "Frau"]);
    // Der Kontaktstatus wird ausgewählt, nicht erraten. Fällt der Abruf aus,
    // bleibt der Default bedienbar — der Dialog muss ohne den Katalog benutzbar
    // sein, so wie er es bei den Anreden schon ist.
    const [statusOptionen, setStatusOptionen] = useState<string[]>([STATUS_DEFAULT]);
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [ergebnis, setErgebnis] = useState<UebernahmeErgebnis | null>(null);
    /**
     * Weitersuchen zu EINEM Namen. Das Register endet bei der Person; erst die
     * Suche nach ihr zeigt, an welchen anderen Gesellschaften sie beteiligt
     * ist — und die sind über ihre Registernummer wieder abrufbar.
     */
    const [recherche, setRecherche] = useState<{ name: string; ort: string | null } | null>(null);

    const sortiert = useMemo(() => {
        const rang = (b: RegisterBeteiligter) => (b.leitung ? 0 : 1);
        return [...daten.beteiligte].sort(
            (a, b) =>
                rang(a) - rang(b) ||
                a.rolle.localeCompare(b.rolle, "de") ||
                anzeigename(a).localeCompare(anzeigename(b), "de"),
        );
    }, [daten.beteiligte]);

    const [gewaehlt, setGewaehlt] = useState<Set<string>>(
        // Über den Index in `sortiert`, nicht in der gefilterten Liste — sonst
        // passte der Schlüssel nicht mehr zur Zeile.
        () => new Set(schluesselWo(sortiert, (b) => b.vorausgewaehlt && waehlbar(b))),
    );
    const [entwuerfe, setEntwuerfe] = useState<Record<string, Entwurf>>({});

    useEffect(() => {
        // Anreden aus dem Katalog; der Dialog bleibt ohne sie benutzbar.
        fetch(apiUrl("contactsalutation"))
            .then((r) => (r.ok ? r.json() : null))
            .then((rows: Array<{ salutation?: string }> | null) => {
                const werte = (rows ?? []).map((r) => r?.salutation).filter(Boolean) as string[];
                if (werte.length) setAnreden(werte);
            })
            .catch(() => undefined);
    }, []);

    useEffect(() => {
        fetch(apiUrl("contactstatus"))
            .then((r) => (r.ok ? r.json() : null))
            .then((rows: Array<{ status?: string }> | null) => {
                const werte = (rows ?? []).map((r) => (r?.status ?? "").trim()).filter(Boolean);
                if (!werte.length) return;
                // Der Default steht oben, danach der Katalog ohne Dublette.
                setStatusOptionen([
                    STATUS_DEFAULT,
                    ...werte
                        // Die alte Katalogzeile „Mensch fürs Unternehmen" bleibt fürs
                        // Archiv stehen, ist aber kein wählbarer Status mehr.
                        .filter((w) => w !== STATUS_DEFAULT && w !== STATUS_MFU_ALT)
                        .sort((a, b) => a.localeCompare(b, "de")),
                ]);
            })
            .catch(() => undefined);
    }, []);

    const umschalten = (k: string, b: RegisterBeteiligter) => {
        if (!waehlbar(b)) return;
        setGewaehlt((alt) => {
            const neu = new Set(alt);
            if (neu.has(k)) neu.delete(k);
            else neu.add(k);
            return neu;
        });
    };

    const auswahl = sortiert.filter((b, i) => gewaehlt.has(schluessel(b, i)));

    const weiterZumPruefen = () => {
        const vor: Record<string, Entwurf> = {};
        sortiert.forEach((b, i) => {
            const k = schluessel(b, i);
            if (!gewaehlt.has(k)) return;
            const vorhanden = b.abgleich === "vorhanden" && !!b.kontaktId;
            vor[k] = entwuerfe[k] ?? {
                anrede: "",
                vorname: b.vorname ?? "",
                nachname: b.nachname ?? b.bezeichnung ?? "",
                geburtsdatum: b.geburtsdatum ?? "",
                anteil: b.anteil ?? "",
                // Sicher abgeleitet gewinnt; sonst der Vorschlag als VORAUSWAHL,
                // die der Anwender hier ändern kann.
                funktion: b.beziehungsfunktion ?? b.rollenVorschlag?.funktion ?? "",
                status: b.art === "person" ? STATUS_DEFAULT : "",
                // Keine Vorauswahl: Die Art wählt der Anwender (Paket A4).
                firmenRolle: "",
                gesperrt: vorhanden,
            };
        });
        setEntwuerfe(vor);
        setSchritt("pruefen");
    };

    const eintraegeBauen = (stand: Record<string, Entwurf>): UebernahmeEintrag[] =>
        sortiert
            .map((b, i) => ({ b, k: schluessel(b, i) }))
            .filter(({ k }) => gewaehlt.has(k))
            // Komplementär-GmbH: weder anlegen noch (in der Anlagemaske) vormerken.
            .filter(({ b, k }) => waehlbar(b) && !alsKomplementaerinBestaetigt(b, stand[k]?.funktion))
            .map(({ b, k }) => {
                const e = stand[k];
                const vorhanden = b.abgleich === "vorhanden" ? b.kontaktId ?? null : null;
                return {
                    rolle: b.rolle,
                    art: b.art,
                    // Bei „aehnlich" wird bewusst NICHT verknüpft: Namensgleichheit
                    // ohne Geburtsdatum ist kein Beleg für dieselbe Person.
                    kontaktId: vorhanden,
                    vorname: b.art === "person" ? e?.vorname ?? b.vorname ?? null : null,
                    nachname: b.art === "person" ? e?.nachname ?? b.nachname ?? null : null,
                    geburtsdatum: b.art === "person" ? e?.geburtsdatum || null : null,
                    bezeichnung: b.art === "organisation" ? e?.nachname ?? b.bezeichnung ?? null : null,
                    anrede: b.art === "person" ? e?.anrede || null : null,
                    anteil: (e?.anteil ?? b.anteil ?? "").trim() || null,
                    // Leer = Ableitung gilt. Nur eine ausdrücklich bestätigte
                    // Rolle wird geschickt.
                    funktion: (e?.funktion ?? "").trim() || null,
                    statusName: b.art === "person" ? (e?.status ?? "").trim() || null : null,
                    // Bei einer Gesellschaft: ihre eigenen Registerangaben, damit
                    // sie in der Akte wieder abrufbar ist.
                    registerNummer: b.art === "organisation" ? b.registerNummer ?? null : null,
                    registergericht: b.art === "organisation" ? b.registergericht ?? null : null,
                    vertretungsbefugnis: b.vertretungsbefugnis ?? null,
                    firmenRolle:
                        firmenRolleNoetig({ art: b.art, beziehungstyp: b.beziehungstyp, funktion: e?.funktion }) &&
                        istFirmenRolle(e?.firmenRolle)
                            ? e!.firmenRolle as FirmenRolle
                            : null,
                };
            });

    const abschliessen = async (stand: Record<string, Entwurf>) => {
        const eintraege = eintraegeBauen(stand);
        if (onEintraege) {
            // Anlagemaske: nichts anlegen, nur zurückgeben.
            onEintraege(eintraege);
            onClose();
            return;
        }
        if (!contactId) return;
        setLaeuft(true);
        setFehler(null);
        try {
            const r = await registerBeteiligteUebernehmen(contactId, eintraege);
            setErgebnis(r);
            setSchritt("ergebnis");
            if (r.angelegt.length) onUebernommen?.();
        } catch (e) {
            setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            setLaeuft(false);
        }
    };

    const setzeEntwurf = (
        k: string,
        feld: "anrede" | "vorname" | "nachname" | "geburtsdatum" | "anteil" | "funktion" | "status" | "firmenRolle",
        wert: string,
    ) => setEntwuerfe((alt) => ({ ...alt, [k]: { ...alt[k], [feld]: wert } }));

    const marke = (b: RegisterBeteiligter) =>
        b.abgleich === "vorhanden"
            ? { text: "Kontakt vorhanden", klasse: s.markeVorhanden }
            : b.abgleich === "aehnlich"
                ? { text: "Namensgleich — bitte prüfen", klasse: s.markeAehnlich }
                : { text: "neu", klasse: s.markeNeu };

    // Was tatsächlich angelegt bzw. vorgemerkt wird — ohne Komplementär-GmbH.
    const anzahlAnzulegen = eintraegeBauen(entwuerfe).length;

    const unvollstaendig = Object.values(entwuerfe).some(
        (e) => !e?.gesperrt && !e?.nachname?.trim(),
    );
    // Gesellschaften als schlichte Gesellschafterin ohne gewählte Art (Paket A4).
    const artFehlt = sortiert.some((b, i) => {
        const e = entwuerfe[schluessel(b, i)];
        return (
            !!e &&
            waehlbar(b) &&
            firmenRolleNoetig({ art: b.art, beziehungstyp: b.beziehungstyp, funktion: e.funktion }) &&
            !istFirmenRolle(e.firmenRolle)
        );
    });

    return createPortal(
        <div className="kvp-rd-scrim" onClick={onClose}>
            <div className="dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">
                                {schritt === "pruefen" ? "Handelsregister · Daten prüfen" : "Handelsregister"}
                            </div>
                            <h2>{daten.firma?.name || "Eingetragene Personen"}</h2>
                        </div>
                        <button className="dm-x" onClick={onClose} aria-label="Schließen">
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
                        </button>
                    </div>
                </div>

                <div className="dm-body">
                    {(daten.hinweise ?? []).map((h, i) => (
                        <div key={i} className={s.hinweis}>{h}</div>
                    ))}
                    {daten.dokument?.documentId && onDokumentOeffnen && (
                        <div className={s.kopfzeile}>
                            <button type="button" className={s.verweis} onClick={onDokumentOeffnen}>
                                {daten.dokument.fileName || "Dokument"} öffnen
                            </button>{" "}
                            — der Beleg, gegen den geprüft wird.
                        </div>
                    )}

                    {schritt === "auswahl" && (
                        <>
                            {daten.firma?.vertretungsregelung && (
                                <div className={s.kopfzeile}>
                                    <strong>Vertretungsregelung:</strong> {daten.firma.vertretungsregelung}
                                </div>
                            )}
                            <div className={s.werkzeuge}>
                                <button
                                    type="button"
                                    className={s.knopf}
                                    onClick={() =>
                                        setGewaehlt(new Set(schluesselWo(sortiert, (b) => b.leitung && waehlbar(b))))
                                    }
                                >
                                    Nur Leitungsorgane
                                </button>
                                <button type="button" className={s.knopf} onClick={() => setGewaehlt(new Set())}>
                                    Auswahl leeren
                                </button>
                                <span className={s.zaehler}>
                                    {gewaehlt.size} von {sortiert.length} ausgewählt
                                </span>
                            </div>
                            <div className={s.liste}>
                                {sortiert.map((b, i) => {
                                    const k = schluessel(b, i);
                                    const aus = !waehlbar(b);
                                    const m = aus ? { text: "wird nicht angelegt", klasse: s.markeAus } : marke(b);
                                    return (
                                        <label key={k} className={mergeClasses(s.zeile, aus && s.zeileAus)}>
                                            <input
                                                type="checkbox"
                                                checked={!aus && gewaehlt.has(k)}
                                                disabled={aus}
                                                onChange={() => umschalten(k, b)}
                                                className={s.haken}
                                                aria-label={aus ? `${anzeigename(b)}: ${VERWALTUNGSGESELLSCHAFT_HINWEIS}` : undefined}
                                            />
                                            <span className={s.inhalt}>
                                                <span className={s.name}>{anzeigename(b)}</span>
                                                {b.art === "organisation" && (
                                                    <span className={s.zaehler}> (Gesellschaft)</span>
                                                )}
                                                <span className={s.meta}>
                                                    {b.rolleAmtlich || b.rolle}
                                                    {b.geburtsdatum ? ` · * ${datum(b.geburtsdatum)}` : ""}
                                                    {b.ort ? ` · ${b.ort}` : ""}
                                                    {b.registerNummer
                                                        ? ` · ${b.registerNummer}${b.registergericht ? ` (AG ${b.registergericht})` : ""}`
                                                        : ""}
                                                    {b.vertretungsbefugnis ? ` · ${b.vertretungsbefugnis}` : ""}
                                                    {b.anteil ? " · " : ""}
                                                    {b.anteil && <span className={s.anteilMarke}>{b.anteil}</span>}
                                                </span>
                                                {aus && <span className={s.verwaltung}>{VERWALTUNGSGESELLSCHAFT_HINWEIS}</span>}
                                            </span>
                                            <span className={mergeClasses(s.marke, m.klasse)}>
                                                {m.text}
                                                {b.beziehungVorhanden ? " · verbunden" : ""}
                                                {/* Die Zeile ist ein <label> um das Kästchen —
                                                    ohne preventDefault schaltet der Klick auf den
                                                    Verweis die Auswahl um. */}
                                                <button
                                                    type="button"
                                                    className={s.weitersuchen}
                                                    onClick={(ev) => {
                                                        ev.preventDefault();
                                                        ev.stopPropagation();
                                                        setRecherche({
                                                            name: anzeigename(b),
                                                            ort: b.ort ?? null,
                                                        });
                                                    }}
                                                >
                                                    weitere Beteiligungen
                                                </button>
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {schritt === "pruefen" && (
                        <>
                            <div className={s.kopfzeile}>
                                Bitte prüfen und bei Bedarf ändern. Anrede und Schreibweise stammen nicht aus
                                dem Register, der Wohnort wird nicht als Adresse übernommen. Bei einem bereits
                                vorhandenen Kontakt bleiben die Stammdaten unberührt — dort wird nur die
                                Verbindung angelegt. Der Kontaktstatus gilt nur für Menschen; wer schon Kunde
                                ist, bleibt Kunde und bekommt den Status zusätzlich.
                            </div>
                            <div className={s.liste}>
                                {sortiert
                                    .map((b, i) => ({ b, k: schluessel(b, i) }))
                                    .filter(({ k }) => entwuerfe[k])
                                    .map(({ b, k }) => {
                                        const e = entwuerfe[k];
                                        const istPerson = b.art === "person";
                                        return (
                                            <div key={k}>
                                                <div className={s.rollenZeile}>
                                                    <strong>{b.rolleAmtlich || b.rolle}</strong>
                                                    {b.ort ? ` · ${b.ort}` : ""}
                                                    {firmenRolleNoetig({ art: b.art, beziehungstyp: b.beziehungstyp, funktion: e.funktion })
                                                        ? " · wird verbunden als Mutterfirma oder Beteiligung (bitte wählen)"
                                                        : ` · wird verbunden als ${b.beziehungstyp}`}
                                                    {b.beziehungsfunktion ? ` (${b.beziehungsfunktion})` : ""}
                                                    {b.abgleich === "aehnlich"
                                                        ? " · namensgleicher Kontakt vorhanden, wird neu angelegt"
                                                        : ""}
                                                </div>
                                                {b.rollenVorschlag && (
                                                    <div className={s.hinweis}>{b.rollenVorschlag.begruendung}</div>
                                                )}
                                                {alsKomplementaerinBestaetigt(b, e.funktion) && (
                                                    <div className={s.hinweis}>
                                                        {VERWALTUNGSGESELLSCHAFT_HINWEIS}: Die Komplementär-GmbH einer
                                                        GmbH &amp; Co. KG ist eine reine Verwaltungsgesellschaft und wird
                                                        nicht als Kontakt geführt.
                                                    </div>
                                                )}
                                                <div className={s.formZeile}>
                                                    {e.gesperrt ? (
                                                        <span className={mergeClasses(s.feld, s.feldBreit)}>
                                                            <span className={s.beschriftung}>Vorhandener Kontakt</span>
                                                            <span className={s.name}>
                                                                {b.kontaktName || e.nachname}
                                                            </span>
                                                        </span>
                                                    ) : istPerson ? (
                                                        <>
                                                            <span className={s.feld}>
                                                                <span className={s.beschriftung}>Anrede</span>
                                                                <select
                                                                    className={s.eingabe}
                                                                    value={e.anrede}
                                                                    onChange={(ev) => setzeEntwurf(k, "anrede", ev.target.value)}
                                                                >
                                                                    <option value="">— keine —</option>
                                                                    {anreden.map((a) => (
                                                                        <option key={a} value={a}>{a}</option>
                                                                    ))}
                                                                </select>
                                                            </span>
                                                            <span className={s.feld}>
                                                                <span className={s.beschriftung}>Vorname</span>
                                                                <input
                                                                    className={s.eingabe}
                                                                    value={e.vorname}
                                                                    onChange={(ev) => setzeEntwurf(k, "vorname", ev.target.value)}
                                                                />
                                                            </span>
                                                            <span className={s.feld}>
                                                                <span className={s.beschriftung}>Nachname</span>
                                                                <input
                                                                    className={s.eingabe}
                                                                    value={e.nachname}
                                                                    onChange={(ev) => setzeEntwurf(k, "nachname", ev.target.value)}
                                                                />
                                                            </span>
                                                            <span className={s.feld}>
                                                                <span className={s.beschriftung}>Geburtsdatum</span>
                                                                <input
                                                                    type="date"
                                                                    className={s.eingabe}
                                                                    value={e.geburtsdatum}
                                                                    onChange={(ev) => setzeEntwurf(k, "geburtsdatum", ev.target.value)}
                                                                />
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className={mergeClasses(s.feld, s.feldBreit)}>
                                                            <span className={s.beschriftung}>Name der Gesellschaft</span>
                                                            <input
                                                                className={s.eingabe}
                                                                value={e.nachname}
                                                                onChange={(ev) => setzeEntwurf(k, "nachname", ev.target.value)}
                                                            />
                                                        </span>
                                                    )}
                                                    {/* Der Anteil gehört zur Verbindung, nicht zum Kontakt —
                                                        deshalb auch bei einem vorhandenen Kontakt bedienbar.
                                                        Aus einem gescannten Dokument kommt er oft gar nicht
                                                        mit; dann trägt ihn der Anwender hier ein. */}
                                                    {b.beziehungstyp === "Gesellschafter" && (
                                                        <span className={s.feld}>
                                                            <span className={s.beschriftung}>Anteil</span>
                                                            <input
                                                                className={s.eingabe}
                                                                value={e.anteil}
                                                                placeholder="z. B. 25,00 %"
                                                                onChange={(ev) => setzeEntwurf(k, "anteil", ev.target.value)}
                                                            />
                                                        </span>
                                                    )}
                                                    {/* Rolle bestätigen: Nur wo ein Vorschlag anliegt — bei
                                                        einer GmbH & Co. KG ist eine beteiligte Gesellschaft
                                                        meist, aber nicht sicher die Komplementärin. */}
                                                    {b.rollenVorschlag && (
                                                        <span className={s.feld}>
                                                            <span className={s.beschriftung}>Rolle</span>
                                                            <select
                                                                className={s.eingabe}
                                                                value={e.funktion}
                                                                onChange={(ev) => setzeEntwurf(k, "funktion", ev.target.value)}
                                                            >
                                                                <option value="">— ohne benannte Rolle —</option>
                                                                {KG_ROLLEN.map((r) => (
                                                                    <option key={r} value={r}>{r}</option>
                                                                ))}
                                                            </select>
                                                        </span>
                                                    )}
                                                    {/* Gesellschaft als schlichte Gesellschafterin: Die Art
                                                        wählt der Anwender — keine Vorauswahl, keine
                                                        Automatik nach der Quote (User 02.10.2026). */}
                                                    {firmenRolleNoetig({ art: b.art, beziehungstyp: b.beziehungstyp, funktion: e.funktion }) && (
                                                        <span className={s.feld}>
                                                            <span className={s.beschriftung}>Verbinden als</span>
                                                            <select
                                                                className={s.eingabe}
                                                                aria-label="Verbinden als"
                                                                value={e.firmenRolle}
                                                                onChange={(ev) => setzeEntwurf(k, "firmenRolle", ev.target.value)}
                                                            >
                                                                <option value="">— bitte wählen —</option>
                                                                {FIRMEN_ROLLEN.map((r) => (
                                                                    <option key={r} value={r}>{FIRMEN_ROLLE_LABEL[r]}</option>
                                                                ))}
                                                            </select>
                                                        </span>
                                                    )}
                                                    {istPerson && (
                                                        <span className={s.feld}>
                                                            <span className={s.beschriftung}>Kontaktstatus</span>
                                                            <select
                                                                className={s.eingabe}
                                                                value={e.status}
                                                                onChange={(ev) => setzeEntwurf(k, "status", ev.target.value)}
                                                            >
                                                                {statusOptionen.map((st) => (
                                                                    <option key={st} value={st}>{st}</option>
                                                                ))}
                                                            </select>
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </>
                    )}

                    {schritt === "ergebnis" && ergebnis && (
                        <div className={s.ergebnis}>
                            <p>
                                {ergebnis.angelegt.length} verbunden
                                {ergebnis.angelegt.some((a) => a.neu)
                                    ? ` (${ergebnis.angelegt.filter((a) => a.neu).length} neu angelegt)`
                                    : ""}
                                .
                            </p>
                            {(ergebnis.uebersprungen ?? []).length > 0 && (
                                <p className={s.zaehler}>
                                    Nicht angelegt ({VERWALTUNGSGESELLSCHAFT_HINWEIS}):{" "}
                                    {(ergebnis.uebersprungen ?? []).map((u) => u.name).join(", ")}
                                </p>
                            )}
                            {ergebnis.fehler.length > 0 && (
                                <ul className={s.ergebnisFehler}>
                                    {ergebnis.fehler.map((f, i) => (
                                        <li key={i}>{f.name}: {f.grund}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {schritt === "pruefen" && artFehlt && (
                        <div className={s.hinweis} role="note">{FIRMEN_ROLLE_HINWEIS}</div>
                    )}
                    {fehler && <div className={s.fehler}>{fehler}</div>}
                </div>

                <div className={mergeClasses("dm-foot", s.fuss)}>
                    {schritt === "auswahl" && (
                        <button
                            type="button"
                            className={mergeClasses(s.knopf, s.knopfStark)}
                            disabled={gewaehlt.size === 0}
                            onClick={weiterZumPruefen}
                        >
                            {`Weiter · ${gewaehlt.size} ausgewählt`}
                        </button>
                    )}
                    {schritt === "pruefen" && (
                        <>
                            <button type="button" className={s.knopf} onClick={() => setSchritt("auswahl")}>
                                Zurück
                            </button>
                            <button
                                type="button"
                                className={mergeClasses(s.knopf, s.knopfStark)}
                                disabled={laeuft || unvollstaendig || artFehlt || anzahlAnzulegen === 0}
                                onClick={() => void abschliessen(entwuerfe)}
                            >
                                {laeuft
                                    ? "Wird übernommen …"
                                    : onEintraege
                                        ? `${anzahlAnzulegen} vormerken`
                                        : `${anzahlAnzulegen} übernehmen`}
                            </button>
                        </>
                    )}
                    <button type="button" className={s.knopf} onClick={onClose}>
                        {schritt === "ergebnis" ? "Fertig" : "Abbrechen"}
                    </button>
                </div>
            </div>
            {recherche && (
                <BeteiligungsrechercheDialog
                    name={recherche.name}
                    ort={recherche.ort}
                    onClose={() => setRecherche(null)}
                />
            )}
        </div>,
        document.body,
    );
}
